import { cn } from "@omni/utils/styles";
import equal from "fast-deep-equal";
import { identity } from "lodash-es";
import { useSelector } from "react-redux";

import { useThunkDispatch } from "@/redux/dispatcher";
import { configureSearchComponent } from "@/redux/modules/value/search/search.actions";
import {
    getSelectedFacetFilters,
    getSelectedNumericFilters,
    useSearchComponentStateSelector
} from "@/redux/modules/value/search/search.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { getHierarchicalFacetName } from "@/services/bffSearch/responseTransformers/mergeHierarchicalFacets.transformer";
import { GetStoreStrategy } from "@/services/store.service";
import { useStore } from "@/utils/hooks/store/useStore.hook";
import { getInvalidationsForComponent } from "@/utils/search/invalidation.utils";
import { SearchComponentOfType } from "@/utils/search/types/searchComponent";

import { FacetType } from "../../../facets/components/facet/@types/facetType.types";
import { ClearSelectedFacetsComponent } from "./clearSelectedFacets.component";
import { SelectedFacetComponent } from "./selectedFacet.component";
import { getLabelMapperByFacetType } from "./selectedFacets.rules";
import { Filters, SelectedFacet } from "./selectedFacets.types";

type SelectedFacetsProps = {
    component: SearchComponentOfType<"FACETS">;
    className?: string;
};

const SelectedFacets = ({ component, className }: SelectedFacetsProps) => {
    const facetFilters = useSelector(getSelectedFacetFilters(component), equal);
    const numericFilters = useSelector(
        getSelectedNumericFilters(component),
        equal
    );

    const { store } = useStore({
        strategy: GetStoreStrategy.Session
    });

    const facets = useSearchComponentStateSelector(component)?.facets;

    const dispatch = useThunkDispatch();
    const { t } = useGranite();

    const mapToSelectedFacet =
        <TFilter extends keyof Filters>(filterType: TFilter) =>
        (filter: Filters[TFilter]) => {
            const facetForFilter = facets?.find((facet) => {
                const facetFilterName =
                    facet.type === FacetType.HIERARCHICAL
                        ? getHierarchicalFacetName(filter.name)
                        : filter.name;

                return facetFilterName === facet.name;
            });
            if (!facetForFilter) return undefined;

            return getLabelMapperByFacetType(facetForFilter.type)({
                facet: facetForFilter,
                filters: { [filterType]: filter },
                dispatch: dispatch,
                component: component,
                t: t,
                preferredStore: store
            });
        };

    const selectedFacets = [
        ...facetFilters.flatMap(mapToSelectedFacet("facetFilter")),
        ...numericFilters.flatMap(mapToSelectedFacet("numericFilter"))
    ].filter(identity) as Array<SelectedFacet>;

    const handleOnClearAllFacets = () => {
        dispatch(
            configureSearchComponent(
                {
                    component: component,
                    facetFilters: [],
                    numericFilters: []
                },
                getInvalidationsForComponent(component)
            )
        );
    };

    return (
        <section className={cn("tw:flex tw:flex-wrap", className)}>
            {!!selectedFacets.length && (
                <span className={"tw:mb-2 tw:mr-2 tw:block"}>
                    {t("search.selectedRefinements.selected")}
                </span>
            )}
            {selectedFacets.map((selectedFacet) => (
                <SelectedFacetComponent
                    key={selectedFacet.label}
                    selectedFacet={selectedFacet}
                />
            ))}
            {selectedFacets.length > 1 && (
                <ClearSelectedFacetsComponent
                    onClear={handleOnClearAllFacets}
                />
            )}
        </section>
    );
};

export { SelectedFacets };
